/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useState } from 'react';
import { Modal, Input, Button } from 'antd';
import type { ModalProps } from 'antd/lib/modal';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import IconButton from '~components/_common/IconButton/IconButton';

const { TextArea } = Input;

interface RejectShareKeyModalProps extends ModalProps {
  // Called when user clicks "Reject"
  onReject: (message: string) => void;
  // Called when user clicks "Reject & Delete"
  onRejectAndDelete: (message: string) => void;
  // Shows loading while API request is in progress
  isSubmitting?: boolean;
  hideSimpleReject?: boolean;
  hideHardReject?: boolean;
}

const RejectShareKeyModal: React.FC<RejectShareKeyModalProps> = ({
  visible,
  onCancel,
  onReject,
  onRejectAndDelete,
  isSubmitting,
  hideSimpleReject,
  hideHardReject,
  ...modalProps
}) => {
  const intl = useIntl();
  const t = (id: string, def: string) =>
    intl.formatMessage({ id, defaultMessage: def });
  const styles = useStyles(styleFn);

  const [messageValue, setMessageValue] = useState('');

  // Clear textarea when modal is closed
  useEffect(() => {
    if (!visible) {
      setMessageValue('');
    }
  }, [visible]);

  const handleReject = () => {
    onReject(messageValue.trim());
  };

  const handleRejectAndDelete = () => {
    onRejectAndDelete(messageValue.trim());
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      title={
        <div css={styles.titleContainer}>
          <IconButton iconStyle="far" icon="ban" backgroundOff />
          {t('cached-share-reject-modal-title', 'Reject key')}
        </div>
      }
      centered
      width={520}
      maskClosable={false}
      destroyOnClose
      {...modalProps}>
      {/* "localization string - Reject the key" */}
      <div css={styles.infoCard}>
        {t('cached-share-reject-key-header', 'Reject the key')}
      </div>

      <div css={styles.fieldBlock}>
        <div css={styles.fieldLabel}>
          {t('cached-share-reject-message-label', 'Rejection message')}
        </div>
        <TextArea
          rows={5}
          value={messageValue}
          onChange={(e) => setMessageValue(e.target.value)}
          placeholder={t(
            'cached-share-reject-message-placeholder',
            'Optional message to the project'
          )}
        />
      </div>

      {/* Footer: [Reject] [Reject & Delete] [Cancel] */}
      <div css={styles.footerActions}>
        {!hideSimpleReject && (
          <Button
            type="primary"
            danger
            ghost
            onClick={handleReject}
            loading={!!isSubmitting}
            disabled={!!isSubmitting}>
            {t('cached-share-reject-modal-ok', 'Reject')}
          </Button>
        )}
        {!hideHardReject && (
          <Button
            type="primary"
            danger
            onClick={handleRejectAndDelete}
            loading={!!isSubmitting}
            disabled={!!isSubmitting}>
            {t('cached-share-reject-delete-modal-ok', 'Reject & Delete')}
          </Button>
        )}
        <Button onClick={onCancel} disabled={!!isSubmitting}>
          {t('cancel', 'Cancel')}
        </Button>
      </div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = () => ({
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 18,
    fontWeight: 700,
  },
  infoCard: {
    border: '1px solid #d9d9d9',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 16,
    background: '#fafafa',
    fontWeight: 500,
  },
  fieldBlock: {
    marginBottom: 24,
  },
  fieldLabel: {
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 600,
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
});

export default RejectShareKeyModal;
